import { useContext } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import isEmpty from 'lodash.isempty';
import {
  Form, FieldSet, ColumnLayout, Column, Typography, Button, ModalBody,
} from '@visionplanner/ui-react-material';
import { SalesOnBoarding } from '../sales.context';
import { generalInformationFormSchema } from './sales-form-validation';
import { createPartner, saveGeneralInformation } from '../sales-onboarding-partner.request';
import { types } from '../sales-onboarding-partner.reducer';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { StyledModalFooter } from '../../styles';

/** Component to create or edit partner general information details */
const GeneralInformation = ({
  officeTypes, originTypes,
}) => {
  const {
    setActiveStep,
    activeStep,
    dispatch,
    state,
  } = useContext(SalesOnBoarding);

  const handleSubmit = useAsyncCallback(async (formValues, { setSubmitting }) => {
    try {
      if (!isEqual(state.generalInformation, formValues)) {
        if (formValues.advisorOriginType !== 2) {
          formValues = { ...formValues, infineLicenceNumber: null };
        }
        if (isEmpty(state.partnerId)) {
          const { partnerId } = await createPartner(formValues);
          dispatch({
            type: types.createPartner,
            payload: { partnerId, formValues },
          });
        } else {
          await saveGeneralInformation(state.partnerId, formValues);
          dispatch({
            type: types.updateGeneralInformation,
            payload: formValues,
          });
        }
      }
      setActiveStep(activeStep + 1);
    } finally {
      setSubmitting(false);
    }
  }, [activeStep, dispatch, setActiveStep, state.generalInformation, state.partnerId]);

  return (
    <>
      <Form
        initialValues={state.generalInformation}
        validateFormSchema={generalInformationFormSchema}
        onSubmit={handleSubmit}
        isInitiallyValid={generalInformationFormSchema.isValidSync(state.generalInformation)}
        dataTa="general-information-form"
        className="full-height flex-column"
      >
        {
          ({
            isValid, isSubmitting, values,
          }) => (
            <>
              <ModalBody>
                <Typography use="h6">Algemene informatie</Typography>
                <ColumnLayout>
                  <Column span={9}>
                    <FieldSet
                      name="firmName"
                      required
                      label="Kantoornaam"
                      dataTa="firm-name"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      label="Type kantoor"
                      name="typeOfOffice"
                      options={officeTypes}
                      placeholder="Selecteer"
                      required
                      dataTa="type-of-office"
                      noOptionsMessage="Geen opties"
                      className="type-of-office"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      label="Herkomst"
                      name="advisorOriginType"
                      options={originTypes}
                      placeholder="Selecteer"
                      required
                      dataTa="type-of-origin"
                      noOptionsMessage="Geen opties"
                      className="type-of-origin"
                    />
                  </Column>
                </ColumnLayout>
                {
                  (values.advisorOriginType === 2)
                  && (
                    <ColumnLayout>
                      <Column span={6}>
                        <FieldSet
                          type="text"
                          label="Infine Licentienummer"
                          name="infineLicenceNumber"
                          dataTa="licence-number"
                          required
                        />
                      </Column>
                    </ColumnLayout>
                  )
                }
              </ModalBody>
              <StyledModalFooter>
                <Button type="submit" dataTa="general-information-step" disabled={!isValid || isSubmitting}>Volgende</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </>
  );
};

export default GeneralInformation;

GeneralInformation.propTypes = {
  /** function to set current or active step */
  setActiveStep: PropTypes.func,
  /** current step */
  activeStep: PropTypes.number,
  /** list of office types */
  officeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** list of service types */
  serviceTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** list of invoice types */
  invoiceTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** list of origin types */
  originTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};
